import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { Title, LaneHeader } from '../common/board/styles/Base';
import InlineInputController from '../common/board/widgets/InlineInput';
import SpaceContentBoardLaneActionsPopupMenu from './space_content_board_lane_actions_popup_menu';
import { useGetOrCreateSpaceContentBoardsQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  useCreateSharedContentEntryMutation,
  useGetSpaceQuery,
  useUpdateSharedContentBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { ActivityObjectType, EntryType, OriginObjectType } from '../../API';
import { SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE } from '../../services/routes';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';

const SpaceContentBoardLaneHeader = ({
  id,
  onDoubleClick,
  onDelete,
  editLaneTitle,
  title,
  laneDraggable,
  updateTitle,
}) => {
  const navigate = useNavigate();

  const { data: space } = useGetSpaceQuery();

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const sharedContentBoard = (sharedContentBoards || []).find((b) =>
    b.lanes.some((l) => l.id === id),
  );

  const [createSharedContentEntry] = useCreateSharedContentEntryMutation();

  const [updateSharedContentBoard] = useUpdateSharedContentBoardMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  /**
   * Add entry to the top of the lane
   */
  const onAddSharedContentEntry = async () => {
    const input = {
      title: '',
      tags: [],
      parentID: sharedContentBoard.id,
      origin: {
        originObjectType: OriginObjectType.CONTENT_ENTRY,
        originObjectID: '00000000-0000-0000-0000-000000000000',
      },
    };
    const { data: sharedContentEntry } = await createSharedContentEntry(input);

    const updatedBoard = {
      ...sharedContentBoard,
      lanes: sharedContentBoard.lanes.map((lane) => {
        if (lane.id !== id) {
          return lane;
        }
        return {
          ...lane,
          entries: [
            {
              id: uuidv4(),
              entryType: EntryType.CONTENT_ENTRY,
              objectID: sharedContentEntry.id,
            },
            ...lane.entries,
          ],
        };
      }),
    };

    await updateSharedContentBoard(updatedBoard);
    reportAccountActivitySignalOperation({
      spaceID: space.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
      lastActivityObjectID: sharedContentBoard.id,
    });
    navigate(
      formatRoute(SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE, {
        sharedContentBoardID: sharedContentBoard.id,
        entryId: sharedContentEntry.id,
      }),
    );
  };

  return (
    <LaneHeader onDoubleClick={onDoubleClick} editLaneTitle={editLaneTitle}>
      <div>
        <Title draggable={laneDraggable}>
          {editLaneTitle ? (
            <InlineInputController
              value={title}
              border
              placeholder="My placeholder"
              resize="vertical"
              onSave={updateTitle}
            />
          ) : (
            title
          )}
        </Title>
      </div>
      <SpaceContentBoardLaneActionsPopupMenu
        onAddSharedContentEntry={onAddSharedContentEntry}
        onDeleteLane={onDelete}
      />
    </LaneHeader>
  );
};

SpaceContentBoardLaneHeader.propTypes = {
  id: PropTypes.string.isRequired,
  editLaneTitle: PropTypes.bool.isRequired,
  laneDraggable: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  updateTitle: PropTypes.func.isRequired,
};

export default SpaceContentBoardLaneHeader;
